import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Ruler } from 'lucide-react';

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduleQuote?: () => void;
  onScheduleMeasurements?: () => void;
  hideScheduleQuote?: boolean;
  hideScheduleMeasurements?: boolean;
}

export default function ScheduleModal({
  isOpen,
  onClose,
  onScheduleQuote,
  onScheduleMeasurements,
  hideScheduleQuote = false,
  hideScheduleMeasurements = false
}: ScheduleModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleScheduleQuote = () => {
    if (onScheduleQuote) {
      onScheduleQuote();
    }
    onClose();
  };

  const handleScheduleMeasurements = () => {
    if (onScheduleMeasurements) {
      onScheduleMeasurements();
    }
    onClose();
  };

  return createPortal(
    <>
      {isOpen && (
        <>
          {/* iOS-style backdrop with blur */}
          <div 
            className="fixed inset-0 transition-opacity duration-200"
            style={{
              zIndex: 100000,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              opacity: 1,
              pointerEvents: 'auto'
            }}
            onClick={onClose}
          />
          
          {/* Modal Content - iOS Style */}
          <div 
            className="fixed inset-0 flex items-center justify-center pointer-events-none z-[100001]"
          >
            <div 
              className="bg-white rounded-[20px] shadow-xl max-w-md w-full mx-4 pointer-events-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button - top right corner */}
              <button 
                onClick={onClose} 
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>

              {/* Content */}
              <div className="px-6 py-5">
                <div className="space-y-3">
                  {/* Schedule Quote */}
                  {!hideScheduleQuote && onScheduleQuote && (
                    <button
                      onClick={handleScheduleQuote}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div
                        className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                        style={{
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      >
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Schedule Quote</div>
                        <div className="text-xs text-gray-500 mt-0.5">Schedule a quote appointment</div>
                      </div>
                    </button>
                  )}
                  
                  {/* Schedule Measurements */}
                  {!hideScheduleMeasurements && onScheduleMeasurements && (
                  <button
                    onClick={handleScheduleMeasurements}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                         style={{ 
                           backdropFilter: 'blur(10px)', 
                           WebkitBackdropFilter: 'blur(10px)'
                         }}>
                      <Ruler className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">Schedule Measurements</div>
                      <div className="text-xs text-gray-500 mt-0.5">Schedule a measurements appointment</div>
                    </div>
                  </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  );
}

